import {
	getTodoScheduleFromTodo,
	getTodoScheduleIri,
	parsePriorityIdFromRef,
} from 'core/utils/todoScheduleEvent';
import { formatDateForPayload } from 'shared/utils/dateUtils';
import { getCategoryIri } from 'module/toDoPage/utils/todoCategoryUtils';
import {
	type TodoStatusOption,
	withTodoCompletionFieldsIfDone,
} from 'module/toDoPage/utils/todoCompletionUtils';

const getRecord = (value: unknown): Record<string, unknown> | null =>
	value && typeof value === 'object' ? (value as Record<string, unknown>) : null;

const getDueTimeForPayload = (value: unknown): string | null => {
	if (typeof value !== 'string' || value.trim() === '') return null;
	return value.trim();
};

export function buildTodoUpdatePayloadFromRecord(
	todo: unknown,
	statusId: number,
	options: {
		workplaceId: number;
		currentUserId: number;
		statuses: TodoStatusOption[];
	}
): Record<string, unknown> {
	const record = getRecord(todo) ?? {};
	const schedule = getTodoScheduleFromTodo(todo) ?? {};

	const rawDueDate =
		record.dueDate ??
		record.due_date ??
		schedule.scheduledDate ??
		schedule.scheduled_date;
	const dueTime = getDueTimeForPayload(
		record.dueTime ?? record.due_time ?? schedule.scheduledTime ?? schedule.scheduled_time
	);
	const categoryIri = getCategoryIri(record.category) || getCategoryIri(schedule.category);
	const priorityId =
		parsePriorityIdFromRef(record.priority) ?? parsePriorityIdFromRef(schedule.priority) ?? 1;
	const note =
		(record.note as { message?: string } | undefined)?.message ??
		(typeof record.note === 'string' ? record.note : schedule.note ?? '');

	const groupObj =
		record.assignedGroup ?? record.group ?? schedule.group ?? schedule.assignedGroup;
	const userObj = record.user ?? record.assignee ?? schedule.assignee;
	const scheduleAssignmentType = String(
		schedule.assignmentType ?? schedule.assignment_type ?? ''
	).toLowerCase();

	const body: Record<string, unknown> = {
		description: String(record.description ?? schedule.description ?? ''),
		log: null,
		dueDate: rawDueDate ? formatDateForPayload(String(rawDueDate)) : '',
		dueTime,
		note: String(note ?? ''),
		organization: '/api/organizations/1',
		geolocation:
			Array.isArray(record.geolocation) && record.geolocation[0]
				? record.geolocation
				: ['28.6139,77.2090'],
		workplace: `/api/workplaces/${options.workplaceId}`,
		priority: `/api/priorities/${priorityId}`,
		status: `/api/statuses/${statusId}`,
		category: categoryIri,
	};

	if (groupObj || scheduleAssignmentType === 'group') {
		const groupId = parsePriorityIdFromRef(groupObj);
		body.assignedGroup = groupId != null ? `/api/checklist_groups/${groupId}` : null;
		body.user = null;
	} else {
		const assigneeId = parsePriorityIdFromRef(userObj);
		body.user = assigneeId != null ? `/api/users/${assigneeId}` : null;
		body.assignedGroup = null;
	}

	const todoScheduleIri = getTodoScheduleIri(todo);
	if (todoScheduleIri) {
		body.todoSchedule = todoScheduleIri;
	}

	return withTodoCompletionFieldsIfDone(
		body,
		statusId,
		options.statuses,
		options.currentUserId
	);
}
